import * as Dialog from '@radix-ui/react-dialog';
import clsx from 'clsx';
import { useEffect, useId, useMemo, useRef, useState, type JSX, type KeyboardEvent } from 'react';

export interface CommandPaletteItem {
  id: string;
  label: string;
  description?: string;
  href: string;
  keywords?: string[];
}

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  items: CommandPaletteItem[];
  onSelect: (item: CommandPaletteItem) => void;
}

export function CommandPalette({
  open,
  onOpenChange,
  items,
  onSelect,
}: CommandPaletteProps): JSX.Element {
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const listId = useId();
  const listRef = useRef<HTMLUListElement | null>(null);

  useEffect(() => {
    if (!open) {
      setQuery('');
      setActiveIndex(0);
    }
  }, [open]);

  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (term.length === 0) {
      return items;
    }
    return items.filter((item) => {
      const haystack = [item.label, item.description, ...(item.keywords ?? [])]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [items, query]);

  useEffect(() => {
    if (!open) {
      return;
    }
    if (filtered.length === 0) {
      setActiveIndex(-1);
      return;
    }
    setActiveIndex(0);
  }, [filtered.length, open, query]);

  useEffect(() => {
    if (!open || activeIndex < 0) {
      return;
    }
    const activeElement = listRef.current?.querySelector<HTMLElement>(
      `[data-command-index="${String(activeIndex)}"]`,
    );
    activeElement?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, open]);

  const activeItem =
    activeIndex >= 0 && activeIndex < filtered.length ? filtered[activeIndex] : null;
  const activeOptionId = activeItem !== null ? `${listId}-option-${activeItem.id}` : undefined;

  const handleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (filtered.length === 0) {
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setActiveIndex((current) => (current + 1) % filtered.length);
        break;
      case 'ArrowUp':
        event.preventDefault();
        setActiveIndex((current) => (current - 1 + filtered.length) % filtered.length);
        break;
      case 'Home':
        event.preventDefault();
        setActiveIndex(0);
        break;
      case 'End':
        event.preventDefault();
        setActiveIndex(filtered.length - 1);
        break;
      case 'Enter': {
        if (activeItem === null) {
          return;
        }
        event.preventDefault();
        onSelect(activeItem);
        break;
      }
      default:
        break;
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm" />
        <Dialog.Content className="fixed left-1/2 top-[20%] z-50 w-[min(640px,92vw)] -translate-x-1/2 rounded-3xl border border-surface-subtle bg-white shadow-2xl focus:outline-none">
          <div className="border-b border-surface-subtle px-5 py-4">
            <Dialog.Title className="text-sm font-semibold uppercase tracking-wide text-text-primary/60">
              Jump to…
            </Dialog.Title>
            <input
              type="text"
              placeholder="Search dashboard destinations"
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
              }}
              onKeyDown={handleInputKeyDown}
              aria-controls={`${listId}-listbox`}
              aria-activedescendant={activeOptionId}
              role="combobox"
              aria-expanded={open}
              className="mt-3 w-full rounded-2xl border border-surface-subtle bg-surface-light px-4 py-3 text-sm text-text-primary shadow-sm focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30"
              autoFocus
            />
          </div>

          <div className="max-h-[60vh] overflow-y-auto p-3">
            {filtered.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-surface-subtle bg-surface-light/60 px-4 py-6 text-center text-sm text-text-primary/60">
                No matches. Try a different search term.
              </div>
            ) : (
              <ul
                ref={listRef}
                id={`${listId}-listbox`}
                role="listbox"
                className="flex flex-col gap-2"
              >
                {filtered.map((item, index) => {
                  const isActive = index === activeIndex;
                  return (
                    <li key={item.id}>
                      <button
                        type="button"
                        id={`${listId}-option-${item.id}`}
                        role="option"
                        aria-selected={isActive}
                        data-command-index={index}
                        onClick={() => {
                          onSelect(item);
                        }}
                        className={clsx(
                          'flex w-full flex-col gap-1 rounded-2xl border border-transparent px-4 py-3 text-left transition',
                          'hover:border-brand-primary/30 hover:bg-brand-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/40',
                          isActive && 'border-brand-primary/40 bg-brand-primary/5',
                        )}
                      >
                        <span className="text-sm font-semibold text-brand-primary">
                          {item.label}
                        </span>
                        {typeof item.description === 'string' && item.description.length > 0 ? (
                          <span className="text-xs text-text-primary/60">{item.description}</span>
                        ) : null}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
